import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PriorityPill } from "@/components/Pills";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, MessageCircle, Send, Check, FileText, CalendarPlus, Paperclip } from "lucide-react";

const DOC_LABELS = {
  aadhaar_pan: "Aadhaar / PAN", bank_statement: "Bank Statement", itr: "ITR",
  gst_reg: "GST Registration", property_doc: "Property Document", business_proof: "Business Proof",
};

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { meta } = useAuth();
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");
  const [replyType, setReplyType] = useState("whatsapp");
  const [fu, setFu] = useState({ date: "", time: "10:00", notes: "", priority: "Warm" });
  const [loan, setLoan] = useState(null);
  const [previews, setPreviews] = useState({});

  const load = () => api.get(`/customers/${id}`).then((r) => { setC(r.data); setLoan(r.data.loan_application); });
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!c?.doc_files) return;
    const urls = [];
    (async () => {
      const next = {};
      for (const [k, fm] of Object.entries(c.doc_files)) {
        if (fm?.content_type?.startsWith("image/")) {
          try {
            const res = await api.get(`/files/${fm.storage_path}`, { responseType: "blob" });
            const u = URL.createObjectURL(res.data);
            next[k] = u; urls.push(u);
          } catch (e) {}
        }
      }
      setPreviews(next);
    })();
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [c?.doc_files]);

  if (!c) return <div className="text-slate-400">Loading…</div>;
  const docCount = Object.values(c.documents || {}).filter(Boolean).length;

  const changeStatus = async (v) => {
    const { data } = await api.patch(`/customers/${id}/status`, { lead_status: v });
    setC(data); toast.success(`Status → ${v}`);
  };
  const toggleDoc = async (k, val) => {
    const { data } = await api.patch(`/customers/${id}/documents`, { documents: { [k]: val } });
    setC(data);
  };
  const saveLoan = async () => {
    const { data } = await api.patch(`/customers/${id}/loan`, { ...loan, amount: Number(loan.amount) || 0 });
    setC(data); toast.success("Loan application saved");
  };
  const addFu = async () => {
    if (!fu.date) return toast.error("Pick a date");
    const { data } = await api.post(`/customers/${id}/followups`, fu);
    setC(data); setFu({ date: "", time: "10:00", notes: "", priority: "Warm" }); toast.success("Follow-up scheduled");
  };
  const completeFu = async (fid) => {
    const { data } = await api.patch(`/customers/${id}/followups/${fid}/complete`);
    setC(data); toast.success("Follow-up completed");
  };
  const addActivity = async (type, direction) => {
    if (!note.trim()) return;
    const { data } = await api.post(`/customers/${id}/activities`, { type, direction, message: note });
    setC(data); setNote("");
  };
  const sendWhatsApp = async () => {
    if (!note.trim()) return toast.error("Type a message first");
    try {
      const { data } = await api.post(`/customers/${id}/whatsapp`, { message: note });
      setC(data); setNote(""); toast.success("WhatsApp message sent");
    } catch (e) {
      const num = (c.whatsapp || c.mobile).replace(/\D/g, "");
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(note)}`, "_blank");
      await addActivity("whatsapp", "out");
      toast.message("Opened WhatsApp via link", { description: "Add your Meta WhatsApp keys for automatic two-way messaging." });
    }
  };
  const uploadDoc = async (k, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/customers/${id}/documents/${k}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC(data); toast.success("Document uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
  };
  const viewFile = async (fileMeta) => {
    try {
      const res = await api.get(`/files/${fileMeta.storage_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Could not open file"); }
  };

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* left column */}
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{c.business_name}</h1>
                <div className="text-sm text-slate-500">{c.contact_person}</div>
              </div>
              <PriorityPill value={c.priority} />
            </div>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" />{c.mobile}</div>
              <div className="flex items-center gap-2"><MessageCircle size={14} className="text-slate-400" />{c.whatsapp || "—"}</div>
              <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" />{c.email || "—"}</div>
              <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" />{c.address || "—"}</div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-400">Category</div><div className="font-semibold">{c.category}</div></div>
              <div><div className="text-xs text-slate-400">Loan Amount</div><div className="font-semibold font-mono">{inr(c.loan_amount)}</div></div>
              <div><div className="text-xs text-slate-400">RM</div><div className="font-semibold">{c.rm_username}</div></div>
              <div><div className="text-xs text-slate-400">Loan Category</div><div className="font-semibold">{c.loan_category || "—"}</div></div>
              <div><div className="text-xs text-slate-400">Mela Interest</div><div className={`font-semibold ${c.mela_interest === "Yes" ? "text-emerald-600" : c.mela_interest === "No" ? "text-slate-500" : ""}`}>{c.mela_interest || "—"}</div></div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-slate-700 flex items-center gap-2"><FileText size={16} />Document Tracker</div>
              <div className="text-sm font-mono font-bold text-emerald-600" data-testid="doc-count">{docCount}/6</div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full mb-4"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(docCount / 6) * 100}%` }} /></div>
            <div className="space-y-2">
              {meta.doc_keys.map((k) => {
                const fileMeta = c.doc_files?.[k];
                return (
                  <div key={k} className="flex items-center gap-3 text-sm" data-testid={`doc-${k}`}>
                    <Checkbox checked={!!c.documents?.[k]} onCheckedChange={(v) => toggleDoc(k, !!v)} />
                    <span className={`flex-1 ${c.documents?.[k] ? "text-slate-800 font-medium" : "text-slate-500"}`}>{DOC_LABELS[k]}</span>
                    {fileMeta && (previews[k]
                      ? <button type="button" onClick={() => viewFile(fileMeta)} data-testid={`view-doc-${k}`}><img src={previews[k]} alt={DOC_LABELS[k]} className="h-9 w-9 rounded object-cover border border-slate-200 hover:ring-2 hover:ring-[#0F4C81] transition-all" /></button>
                      : <button type="button" onClick={() => viewFile(fileMeta)} data-testid={`view-doc-${k}`} className="h-9 px-2 rounded border border-slate-200 flex items-center gap-1 text-xs text-rose-600 font-semibold hover:bg-slate-50"><FileText size={13} />File</button>)}
                    <label className="cursor-pointer text-slate-400 hover:text-[#0F4C81]" data-testid={`upload-doc-${k}`} title="Upload / replace file">
                      <Paperclip size={15} />
                      <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => e.target.files[0] && uploadDoc(k, e.target.files[0])} />
                    </label>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* right columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* pipeline */}
          <Card className="p-6">
            <div className="text-sm font-bold text-slate-700 mb-4">Lead Status Pipeline</div>
            <div className="flex items-center gap-1 overflow-x-auto pb-2">
              {meta.lead_stages.map((s, i) => {
                const active = meta.lead_stages.indexOf(c.lead_status) >= i;
                const current = c.lead_status === s;
                return (
                  <div key={s} className="flex items-center shrink-0">
                    <button data-testid={`stage-${s}`} onClick={() => changeStatus(s)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${current ? "bg-[#0F4C81] text-white" : active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}>
                      {s}
                    </button>
                    {i < meta.lead_stages.length - 1 && <div className={`w-4 h-0.5 ${active ? "bg-emerald-300" : "bg-slate-200"}`} />}
                  </div>
                );
              })}
            </div>
          </Card>

          <Tabs defaultValue="loan">
            <TabsList data-testid="profile-tabs">
              <TabsTrigger value="loan" data-testid="tab-loan">Loan Application</TabsTrigger>
              <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="loan">
              <Card className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <F label="Loan Category">
                    <Select value={loan.loan_category || ""} onValueChange={(v) => setLoan({ ...loan, loan_category: v })}>
                      <SelectTrigger data-testid="loan-category"><SelectValue placeholder="Select loan category" /></SelectTrigger>
                      <SelectContent>{(meta.loan_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Amount (₹)"><Input data-testid="loan-amount" type="number" value={loan.amount} onChange={(e) => setLoan({ ...loan, amount: e.target.value })} /></F>
                  <F label="Interest Rate (%)"><Input type="number" value={loan.interest_rate} onChange={(e) => setLoan({ ...loan, interest_rate: Number(e.target.value) })} /></F>
                  <F label="Tenure (months)"><Input type="number" value={loan.tenure_months} onChange={(e) => setLoan({ ...loan, tenure_months: Number(e.target.value) })} /></F>
                  <F label="Bank Partner"><Input value={loan.bank_partner} onChange={(e) => setLoan({ ...loan, bank_partner: e.target.value })} /></F>
                  <F label="Application Ref #"><Input value={loan.application_ref} onChange={(e) => setLoan({ ...loan, application_ref: e.target.value })} /></F>
                  <F label="Status">
                    <Select value={loan.status} onValueChange={(v) => setLoan({ ...loan, status: v })}>
                      <SelectTrigger data-testid="loan-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Draft", "Submitted", "Under Review", "Approved", "Rejected", "Disbursed"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <Button onClick={saveLoan} data-testid="save-loan-btn" className="mt-5 bg-[#0F4C81] hover:bg-[#0A3A66]">Save Application</Button>
              </Card>
            </TabsContent>

            <TabsContent value="followups">
              <Card className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                  <F label="Next Date"><Input type="date" data-testid="followup-date" value={fu.date} onChange={(e) => setFu({ ...fu, date: e.target.value })} /></F>
                  <F label="Time"><Input type="time" value={fu.time} onChange={(e) => setFu({ ...fu, time: e.target.value })} /></F>
                  <F label="Priority">
                    <Select value={fu.priority} onValueChange={(v) => setFu({ ...fu, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{meta.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <Button onClick={addFu} data-testid="add-followup-btn" className="bg-emerald-600 hover:bg-emerald-700"><CalendarPlus size={16} className="mr-2" />Schedule</Button>
                </div>
                <Textarea placeholder="Notes…" value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} className="mt-3" />
                <div className="mt-5 space-y-2">
                  {(c.followups || []).map((f) => (
                    <div key={f.id} className={`flex items-center gap-3 p-3 rounded-lg border ${f.status === "complete" ? "bg-slate-50 border-slate-100" : "bg-white border-slate-200"}`} data-testid={`followup-${f.id}`}>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800">{f.date} {f.time}</div>
                        <div className="text-xs text-slate-500">{f.notes || "No notes"}</div>
                      </div>
                      <PriorityPill value={f.priority} />
                      {f.status === "pending" ? (
                        <Button size="sm" variant="outline" onClick={() => completeFu(f.id)} data-testid={`complete-fu-${f.id}`}><Check size={14} className="mr-1" />Done</Button>
                      ) : <span className="text-xs text-emerald-600 font-semibold">Completed</span>}
                    </div>
                  ))}
                  {(!c.followups || c.followups.length === 0) && <div className="text-sm text-slate-400 text-center py-4">No follow-ups scheduled.</div>}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="activity">
              <Card className="p-6">
                <div className="flex gap-2 mb-3">
                  <Select value={replyType} onValueChange={setReplyType}>
                    <SelectTrigger className="w-36" data-testid="activity-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{["note", "whatsapp", "sms", "email"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a note or message…" data-testid="activity-input" onKeyDown={(e) => e.key === "Enter" && addActivity(replyType, "out")} />
                  <Button onClick={() => addActivity(replyType, "out")} data-testid="send-activity-btn" className="bg-[#0F4C81] hover:bg-[#0A3A66]"><Send size={16} /></Button>
                  <Button variant="outline" onClick={sendWhatsApp} data-testid="send-whatsapp-btn" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"><MessageCircle size={16} /></Button>
                  <Button variant="outline" onClick={() => addActivity(replyType, "in")} title="Log an incoming reply" data-testid="log-reply-btn">Log Reply</Button>
                </div>
                <div className="space-y-3 mt-4 max-h-[420px] overflow-y-auto">
                  {[...(c.activities || [])].reverse().map((a) => (
                    <div key={a.id} className={`flex ${a.direction === "in" ? "justify-start" : "justify-end"}`} data-testid={`activity-${a.id}`}>
                      <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${a.type === "system" ? "bg-slate-100 text-slate-500 text-xs mx-auto" : a.direction === "in" ? "bg-white border border-slate-200" : "bg-[#0F4C81] text-white"}`}>
                        {a.type !== "system" && <div className={`text-[10px] uppercase font-bold mb-0.5 ${a.direction === "in" ? "text-slate-400" : "text-emerald-300"}`}>{a.type} · {a.direction === "in" ? "received" : a.author}</div>}
                        <div>{a.message}</div>
                        <div className={`text-[10px] mt-1 ${a.direction === "in" || a.type === "system" ? "text-slate-400" : "text-emerald-200"}`}>{new Date(a.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => <div><Label className="mb-1.5 block">{label}</Label>{children}</div>;
